import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearAccessToken, getAccessToken, setAccessToken } from '../lib/authStorage';
import { refreshAccessToken, setUnauthorizedHandler } from '../lib/apiClient';
import { getCurrentUser, logoutUser } from '../services/userService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const logout = useCallback(async ({ shouldCallApi = true } = {}) => {
    clearAccessToken();
    localStorage.removeItem('userName');
    setCurrentUser(null);
    setIsAuthenticated(false);

    if (!shouldCallApi) {
      return;
    }

    try {
      await logoutUser();
    } catch {
      // Local logout should still succeed if the API request fails.
    }
  }, []);

  const loadAuthState = useCallback(async () => {
    let token = getAccessToken();

    if (!token) {
      try {
        token = await refreshAccessToken();
      } catch {
        await logout({ shouldCallApi: false });
        setIsAuthLoading(false);
        return;
      }
    }

    try {
      const user = await getCurrentUser(token);
      setCurrentUser(user);
      setIsAuthenticated(true);
    } catch {
      await logout({ shouldCallApi: false });
    } finally {
      setIsAuthLoading(false);
    }
  }, [logout]);

  const login = useCallback(async (accessToken) => {
    setAccessToken(accessToken);
    setIsAuthLoading(true);
    await loadAuthState();
  }, [loadAuthState]);

  const updateCurrentUserState = useCallback((updatedUser) => {
    setCurrentUser(updatedUser);
    if (updatedUser?.name) {
      localStorage.setItem('userName', updatedUser.name);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout({ shouldCallApi: false }));
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    loadAuthState();
  }, [loadAuthState]);

  const value = useMemo(() => ({
    accessToken: getAccessToken(),
    currentUser,
    isAuthenticated,
    isAuthLoading,
    login,
    logout,
    refreshAuthState: loadAuthState,
    updateCurrentUser: updateCurrentUserState,
  }), [currentUser, isAuthenticated, isAuthLoading, login, loadAuthState, logout, updateCurrentUserState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
